/**
 * A site's servers (D9, D16, D21, D30): role, the cloud link and last contact,
 * the peer link to the other server of the pair (its last contact on hover) and
 * the witness in force and whether it is reachable — both from the site's
 * redundancy overview, the pair reports the nodes send — the package versions,
 * the certificate's expiry and the last health report. A server whose LAN
 * address its peer cannot reach (Docker Desktop's VM, the docker0 bridge) says
 * so, with the way out; one the cloud says is connected but this browser cannot
 * reach by its name says what is probably wrong (`ReachHintNote`: DNS rebinding
 * protection, an untrusted certificate, no answer, another network). Actions: make the backup
 * main (swap roles), replace a server (revoke its key and get a code for its
 * successor), remove one for good (no successor).
 *
 * Below the live servers, each code issued and not used yet is a row of its
 * own — "waiting for the server to connect…", with what it will enrol and when
 * it expires, and a Cancel — so a server that never enrols is visible, not
 * silently missing. Revoked (replaced or removed) servers are kept apart,
 * collapsed under "Replaced servers", greyed and marked revoked, each with a
 * "Delete record".
 *
 * A live server can be renamed (the pencil by its name) and given a LAN address
 * (`LanAddressControl`: auto, one it reported, or one typed in; `ACP_LAN_IP` on
 * the server wins) — cleanup wave G's `updateNode`.
 */
import type { SitePairNodeWire } from '@acp/api-contract/pairStatus.js';
import type { PendingEnrolmentWire, SiteNodeWire } from '@acp/api-contract/sites.js';
import { ChevronDownIcon, ChevronRightIcon, LoaderIcon, PencilIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { agoText, certState, healthText, lanWarningText, peerLinkView, pendingText, shortHash, splitNodes, untilText, witnessStateText } from '../../lib/admin/view.js';
import { UNKNOWN_HINT } from '../../lib/openEnum.js';
import type { ReachHint } from '../../connectivity/reachHint.js';
import { ReachHintNote } from '../ReachHintNote.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { LanAddressControl } from './LanAddressControl.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type NodesTableProps = {
  readonly nodes: readonly SiteNodeWire[];
  /** Codes issued for the site and not used yet (never the code itself). */
  readonly pendingEnrolments: readonly PendingEnrolmentWire[];
  /** The servers as the site's redundancy overview has them (their pair reports): the peer-link and witness columns. */
  readonly pairNodes: readonly SitePairNodeWire[];
  readonly nowMs: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly pending: boolean;
  readonly onRetry: () => void;
  readonly onMakeMain: (nodeId: string) => void;
  readonly onReplace: (nodeId: string) => void;
  readonly onRemove: (nodeId: string) => void;
  /** Forget a revoked server's record. */
  readonly onDelete: (nodeId: string) => void;
  readonly onCancelEnrolment: (codeId: string) => void;
  /** Per node id: why this browser cannot reach a connected server by its name (the connectivity manager's view); absent: none. */
  readonly reachHints?: Readonly<Record<string, ReachHint>>;
  /** "Connect to local servers" beside a Local Network Access hint: probe them now, from this click. */
  readonly onConnectLocal?: () => void;
  /** Rename a live server (the pencil by its name); absent: no rename here. */
  readonly onRename?: (nodeId: string, label: string) => void;
  /** Choose a live server's LAN address (null: auto) — the "LAN address" control; absent: the address alone. */
  readonly onSetLanIp?: (nodeId: string, lanIp: string | null) => void;
};

// `unknown`: a link state from a newer cloud (D37) — neutral.
const LINK_BADGE = { connected: 'success', disconnected: 'offline', 'never-connected': 'outline', unknown: 'secondary' } as const;
const PEER_BADGE = { connected: 'success', disconnected: 'offline', none: 'outline' } as const;
const CERT_BADGE = { ok: 'outline', warning: 'warning', expired: 'destructive', none: 'outline' } as const;
const COLUMNS = 9;

/** A live row's server edits (the rename and the LAN address); null on a revoked row, or where the caller offers none. */
type NodeEdits = {
  readonly pending: boolean;
  readonly onRename: ((label: string) => void) | null;
  readonly onSetLanIp: ((lanIp: string | null) => void) | null;
};

type RowProps = {
  readonly node: SiteNodeWire;
  readonly hint: ReachHint | null;
  readonly onConnectLocal: (() => void) | null;
  readonly edits: NodeEdits;
  readonly pair: SitePairNodeWire | null;
  readonly nowMs: number;
  readonly pending: boolean;
  readonly onMakeMain: (nodeId: string) => void;
  readonly onReplace: (nodeId: string) => void;
  readonly onRemove: (nodeId: string) => void;
};

/** The server's name, and — offered — a pencil that turns it into a field (Enter saves, Escape cancels). */
function NodeLabel({ label, pending, onRename }: { readonly label: string; readonly pending: boolean; readonly onRename: ((label: string) => void) | null }) {
  const [draft, setDraft] = useState<string | null>(null);
  if (draft === null || onRename === null) {
    return (
      <div className="flex items-center gap-1">
        <span className="font-medium" data-testid="node-label">
          {label}
        </span>
        {onRename === null ? null : (
          <Button size="icon" variant="ghost" className="size-6" aria-label={`Rename ${label}`} disabled={pending} onClick={() => setDraft(label)} data-testid="rename-node">
            <PencilIcon className="size-3.5" />
          </Button>
        )}
      </div>
    );
  }
  const trimmed = draft.trim();
  const valid = trimmed !== '' && trimmed.length <= 100 && trimmed !== label;
  const save = (): void => {
    if (!valid) return;
    onRename(trimmed);
    setDraft(null);
  };
  return (
    <div className="flex items-center gap-1" data-testid="rename-form">
      <Input
        aria-label="Server name"
        className="h-7 w-44 text-sm"
        value={draft}
        maxLength={100}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setDraft(null);
        }}
        data-testid="rename-input"
      />
      <Button size="sm" className="h-7" disabled={!valid || pending} onClick={save} data-testid="rename-save">
        Save
      </Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => setDraft(null)} data-testid="rename-cancel">
        Cancel
      </Button>
    </div>
  );
}

function NodeIdentity({ node, hint, onConnectLocal, edits }: { readonly node: SiteNodeWire; readonly hint: ReachHint | null; readonly onConnectLocal: (() => void) | null; readonly edits: NodeEdits | null }) {
  const lanWarning = lanWarningText(node);
  return (
    <>
      <NodeLabel label={node.label} pending={edits?.pending ?? false} onRename={edits?.onRename ?? null} />
      {edits?.onSetLanIp === null || edits?.onSetLanIp === undefined ? (
        <div className="font-mono text-xs text-muted-foreground">
          {node.lanIp ?? '–'} · {node.arch}
        </div>
      ) : (
        <>
          <div className="font-mono text-xs text-muted-foreground">{node.arch}</div>
          <LanAddressControl node={node} pending={edits.pending} onSetLanIp={edits.onSetLanIp} />
        </>
      )}
      {node.dnsName === null ? null : <div className="font-mono text-xs text-muted-foreground">{node.dnsName}</div>}
      {lanWarning === null ? null : (
        <div className="mt-1 flex max-w-xs items-start gap-1 text-xs text-warning" data-testid="lan-warning">
          <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{lanWarning}</span>
        </div>
      )}
      {hint === null ? null : (
        <div className="mt-1">
          <ReachHintNote hint={hint} {...(onConnectLocal === null ? {} : { onConnectLocal })} />
        </div>
      )}
    </>
  );
}

function PeerLink({ node, pair }: { readonly node: SiteNodeWire; readonly pair: SitePairNodeWire | null }) {
  const view = peerLinkView(node, pair);
  if (view === null) return <span className="text-muted-foreground">–</span>;
  return (
    <Badge variant={PEER_BADGE[view.tone]} title={view.title} data-testid="peer-link" data-tone={view.tone}>
      {view.text}
    </Badge>
  );
}

function NodeActions({ node, pending, onMakeMain, onReplace, onRemove }: Omit<RowProps, 'nowMs' | 'pair' | 'hint' | 'onConnectLocal' | 'edits'>) {
  return (
    <div className="flex justify-end gap-1">
      {node.role === 'backup' ? (
        <Button size="sm" variant="outline" disabled={pending} onClick={() => onMakeMain(node.nodeId)} data-testid="make-main">
          Make main
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" disabled={pending} onClick={() => onReplace(node.nodeId)} data-testid="replace-node">
        Replace
      </Button>
      <Button size="sm" variant="ghost" className="text-destructive" disabled={pending} onClick={() => onRemove(node.nodeId)} data-testid="remove-node">
        Remove
      </Button>
    </div>
  );
}

function NodeRow({ node, hint, onConnectLocal, edits, pair, nowMs, pending, onMakeMain, onReplace, onRemove }: RowProps) {
  const cert = certState(node.certExpiresAt, nowMs);
  const health = healthText(node.health);
  return (
    <TableRow data-testid="node-row" data-role={node.role} data-status={node.status}>
      <TableCell>
        <NodeIdentity node={node} hint={hint} onConnectLocal={onConnectLocal} edits={edits} />
      </TableCell>
      <TableCell>
        <Badge variant={node.role === 'main' ? 'default' : 'secondary'}>{node.role}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={LINK_BADGE[node.linkState]} title={node.linkState === 'unknown' ? UNKNOWN_HINT : undefined}>
          {node.linkState}
        </Badge>
        <div className="text-xs text-muted-foreground">seen {agoText(node.lastSeen, nowMs)}</div>
      </TableCell>
      <TableCell>
        <PeerLink node={node} pair={pair} />
      </TableCell>
      <TableCell className="text-sm" data-testid="witness-state">
        {witnessStateText(pair, node.health) ?? '–'}
      </TableCell>
      <TableCell className="font-mono text-xs">{node.packageHashes.length === 0 ? '–' : node.packageHashes.map(shortHash).join(' ')}</TableCell>
      <TableCell>
        <Badge variant={CERT_BADGE[cert.tone]} data-testid="cert-state" data-tone={cert.tone}>
          {cert.text}
        </Badge>
      </TableCell>
      <TableCell className="text-xs">{health ?? <span className="text-muted-foreground">no report yet</span>}</TableCell>
      <TableCell>
        <NodeActions node={node} pending={pending} onMakeMain={onMakeMain} onReplace={onReplace} onRemove={onRemove} />
      </TableCell>
    </TableRow>
  );
}

function PendingRow({ enrolment, nodes, nowMs, pending, onCancel }: { readonly enrolment: PendingEnrolmentWire; readonly nodes: readonly SiteNodeWire[]; readonly nowMs: number; readonly pending: boolean; readonly onCancel: (codeId: string) => void }) {
  return (
    <TableRow data-testid="pending-row" data-code={enrolment.codeId}>
      <TableCell>
        <div className="font-medium">{pendingText(enrolment, nodes)}</div>
        <div className="font-mono text-xs text-muted-foreground">code {enrolment.codeId}</div>
      </TableCell>
      <TableCell colSpan={COLUMNS - 2}>
        <div className="flex items-center gap-2 text-sm">
          <LoaderIcon className="size-4 motion-safe:animate-spin text-muted-foreground" aria-hidden="true" />
          <span>Waiting for the server to connect…</span>
        </div>
        <div className="text-xs text-muted-foreground">
          code issued {agoText(enrolment.createdAt, nowMs)}, expires {untilText(enrolment.expiresAt, nowMs)} — run its install command on the new host
        </div>
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onCancel(enrolment.codeId)} data-testid="cancel-code">
            Cancel code
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function RevokedRow({ node, nowMs, pending, onDelete }: { readonly node: SiteNodeWire; readonly nowMs: number; readonly pending: boolean; readonly onDelete: (nodeId: string) => void }) {
  return (
    <TableRow data-testid="node-row" data-role={node.role} data-status={node.status} className="opacity-60">
      <TableCell>
        <NodeIdentity node={node} hint={null} onConnectLocal={null} edits={null} />
      </TableCell>
      <TableCell>
        <Badge variant="offline">revoked</Badge>
        <div className="text-xs text-muted-foreground">was {node.role}</div>
      </TableCell>
      <TableCell colSpan={COLUMNS - 3} className="text-xs text-muted-foreground">
        revoked {agoText(node.revokedAt, nowMs)} · last seen {agoText(node.lastSeen, nowMs)} · its key is refused for good
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onDelete(node.nodeId)} data-testid="delete-node">
            Delete record
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function Header() {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>Server</TableHead>
        <TableHead>Role</TableHead>
        <TableHead>Cloud link</TableHead>
        <TableHead>Peer link</TableHead>
        <TableHead>Witness</TableHead>
        <TableHead>Versions</TableHead>
        <TableHead>Certificate</TableHead>
        <TableHead>Health</TableHead>
        <TableHead className="text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function Replaced({ nodes, nowMs, pending, onDelete }: { readonly nodes: readonly SiteNodeWire[]; readonly nowMs: number; readonly pending: boolean; readonly onDelete: (nodeId: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="grid gap-2">
      <Button variant="ghost" size="sm" className="justify-self-start" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="show-revoked">
        {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
        Replaced servers ({nodes.length})
      </Button>
      {open ? (
        <Table data-testid="revoked-table">
          <TableBody>
            {nodes.map((n) => (
              <RevokedRow key={n.nodeId} node={n} nowMs={nowMs} pending={pending} onDelete={onDelete} />
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}

export function NodesTable({ nodes, pendingEnrolments, pairNodes, nowMs, loading, error, pending, onRetry, onMakeMain, onReplace, onRemove, onDelete, onCancelEnrolment, reachHints, onConnectLocal, onRename, onSetLanIp }: NodesTableProps) {
  const editsOf = (nodeId: string): NodeEdits => ({
    pending,
    onRename: onRename === undefined ? null : (label) => onRename(nodeId, label),
    onSetLanIp: onSetLanIp === undefined ? null : (lanIp) => onSetLanIp(nodeId, lanIp),
  });
  const { active, revoked } = splitNodes(nodes);
  const listed = active.length + pendingEnrolments.length;
  return (
    <div className="grid gap-3" data-testid="nodes-table">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load servers: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {loading && nodes.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Loading servers…</p> : null}
      {!loading && error === null && listed === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="nodes-empty">
          No servers yet. “Add server” shows the command that installs one.
        </p>
      ) : null}
      {listed > 0 ? (
        <Table>
          <Header />
          <TableBody>
            {active.map((n) => (
              <NodeRow key={n.nodeId} node={n} hint={reachHints?.[n.nodeId] ?? null} onConnectLocal={onConnectLocal ?? null} edits={editsOf(n.nodeId)} pair={pairNodes.find((p) => p.nodeId === n.nodeId) ?? null} nowMs={nowMs} pending={pending} onMakeMain={onMakeMain} onReplace={onReplace} onRemove={onRemove} />
            ))}
            {pendingEnrolments.map((e) => (
              <PendingRow key={e.codeId} enrolment={e} nodes={nodes} nowMs={nowMs} pending={pending} onCancel={onCancelEnrolment} />
            ))}
          </TableBody>
        </Table>
      ) : null}
      {revoked.length > 0 ? <Replaced nodes={revoked} nowMs={nowMs} pending={pending} onDelete={onDelete} /> : null}
    </div>
  );
}
